import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import PlayCircleOutlineOutlined from "@mui/icons-material/PlayCircleOutlineOutlined";
import HourglassEmptyOutlined from "@mui/icons-material/HourglassEmptyOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import {
  formatJobDate,
  jobCategoryLabel,
  jobModelLabel,
  jobStatusMeta,
  truncatePrompt,
} from "../utils/jobLabels";

export default function VideoLibraryCard({ job, lang, onClick, onDelete }) {
  const theme = useTheme();
  const { label: statusLabel, color: statusColor } = jobStatusMeta(job.status, lang);
  const hasVideo = job.status === "completed" && Boolean(job.video_url);
  const isProcessing = job.status === "pending" || job.status === "generating";

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardActionArea onClick={() => onClick?.(job)} sx={{ flex: 1, alignItems: "stretch" }}>
        <Box
          sx={{
            position: "relative",
            pt: "56.25%",
            bgcolor: alpha(theme.palette.common.black, 0.88),
          }}
        >
          {hasVideo ? (
            <Box
              component="video"
              src={job.video_url}
              muted
              playsInline
              preload="metadata"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "grey.500",
              }}
            >
              {job.status === "failed" ? (
                <ErrorOutlineOutlined sx={{ fontSize: 40 }} />
              ) : isProcessing ? (
                <HourglassEmptyOutlined sx={{ fontSize: 40 }} />
              ) : (
                <PlayCircleOutlineOutlined sx={{ fontSize: 40 }} />
              )}
            </Box>
          )}
          <Chip
            label={statusLabel}
            color={statusColor}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              fontWeight: 700,
            }}
          />
        </Box>
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={800} noWrap>
            {job.title || jobCategoryLabel(job.category_id, lang)}
          </Typography>
          {job.title ? (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {jobCategoryLabel(job.category_id, lang)}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {jobModelLabel(job.ui_model, lang)}
          </Typography>
          {job.prompt ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {truncatePrompt(job.prompt, 100)}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: "block" }}>
            {formatJobDate(job.created_at)}
          </Typography>
        </CardContent>
      </CardActionArea>
      {onDelete ? (
        <Tooltip title={lang?.videoLibraryDelete || "Delete"}>
          <IconButton
            size="small"
            aria-label={lang?.videoLibraryDelete || "Delete"}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(job);
            }}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 2,
              bgcolor: alpha(theme.palette.error.main, 0.92),
              color: "common.white",
              "&:hover": {
                bgcolor: theme.palette.error.dark,
              },
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Card>
  );
}

export function VideoLibraryCardSkeleton() {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <Box sx={{ pt: "56.25%", position: "relative" }}>
        <Skeleton
          variant="rectangular"
          sx={{ position: "absolute", inset: 0, height: "100%", borderRadius: 0 }}
        />
      </Box>
      <CardContent>
        <Skeleton width="60%" />
        <Skeleton width="40%" sx={{ mt: 1 }} />
        <Skeleton height={40} sx={{ mt: 1 }} />
      </CardContent>
    </Card>
  );
}
